import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-br from-mimo-primary to-mimo-accent text-white py-20">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-white/90">
            Last Updated: April 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-slate max-w-none">
            
            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Introduction</h2>
            <p className="text-slate-600 mb-4">
              mimoIT Technology Solutions ("mimoIT," "we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <a href="https://mimoit.com" className="text-mimo-primary hover:text-mimo-accent">mimoit.com</a>, use our services, or interact with us.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-bold font-heading text-slate-900 mt-6 mb-3">Personal Information</h3>
            <p className="text-slate-600 mb-4">
              We may collect personal information that you voluntarily provide to us, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Name, email address, phone number, and company name</li>
              <li>Business address and contact information</li>
              <li>Account credentials and authentication information</li>
              <li>Payment and billing information</li>
              <li>Service preferences and communication preferences</li>
              <li>Support ticket information and service requests</li>
            </ul>

            <h3 className="text-xl font-bold font-heading text-slate-900 mt-6 mb-3">Technical Information</h3>
            <p className="text-slate-600 mb-4">
              We automatically collect certain information when you visit our website:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>IP address, browser type, and device information</li>
              <li>Pages visited, time spent on pages, and navigation paths</li>
              <li>Referring website addresses and search terms</li>
              <li>System and network performance data</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="text-xl font-bold font-heading text-slate-900 mt-6 mb-3">Service Data</h3>
            <p className="text-slate-600 mb-4">
              When you use our IT services, we may collect:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Network and system logs</li>
              <li>Security events and incident reports</li>
              <li>System performance metrics</li>
              <li>Configuration and inventory data</li>
              <li>Backup and recovery information</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Service Delivery:</strong> Provide, maintain, and improve our IT services</li>
              <li><strong>Communication:</strong> Respond to inquiries, send service notifications, and provide support</li>
              <li><strong>Security:</strong> Monitor systems, detect threats, and prevent security incidents</li>
              <li><strong>Compliance:</strong> Meet legal and regulatory requirements</li>
              <li><strong>Business Operations:</strong> Process payments, manage accounts, and maintain records</li>
              <li><strong>Marketing:</strong> Send promotional email communications with your consent. SMS messaging is used only for conversational business communications unless a separate SMS program is expressly offered and consented to.</li>
              <li><strong>Analytics:</strong> Analyze usage patterns and improve our services</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">SMS Communications & Mobile Data</h2>
            <p className="text-slate-600 mb-4">
              mimoIT Technology Solutions offers conversational SMS communications to customers who voluntarily provide their mobile telephone number during a business interaction, service request, website form submission, telephone conversation, or other direct communication.
            </p>
            <p className="text-slate-600 mb-4">SMS messages may include:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>IT service updates</li>
              <li>Appointment scheduling and confirmations</li>
              <li>Support ticket updates</li>
              <li>Security alerts</li>
              <li>Maintenance notifications</li>
              <li>Responses to customer inquiries</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Message frequency varies based on customer interactions but generally will not exceed five (5) messages per month.
            </p>
            <p className="text-slate-600 mb-4">
              Message and data rates may apply.
            </p>
            <p className="text-slate-600 mb-4">
              Customers may opt out of SMS communications at any time by replying <strong>STOP</strong> to any message. Customers may reply <strong>HELP</strong> for assistance or contact us at <a href="mailto:info@mimoit.com" className="text-mimo-primary hover:text-mimo-accent">info@mimoit.com</a> or <a href="tel:201-473-4443" className="text-mimo-primary hover:text-mimo-accent">(201) 473-4443</a>.
            </p>

            <h3 className="text-xl font-bold font-heading text-slate-900 mt-6 mb-3">Important Disclosure</h3>
            <p className="text-slate-600 mb-4">
              Mobile opt-in data, SMS consent records, and mobile telephone numbers collected for SMS communications will <strong>NOT</strong> be sold, rented, shared, transferred, or disclosed to third parties or affiliates for their marketing or promotional purposes.
            </p>
            <p className="text-slate-600 mb-4">
              SMS consent information is used solely for providing conversational customer communications requested by the customer.
            </p>
            <p className="text-slate-600 mb-4">
              We may utilize trusted service providers solely for the purpose of transmitting SMS messages and operating our communications platform. These providers are contractually prohibited from using your mobile information for their own marketing or promotional purposes.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Information Sharing and Disclosure</h2>
            <p className="text-slate-600 mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Service Providers:</strong> Trusted third-party vendors who assist us in providing our services, including cloud hosting, payment processing, email delivery, communications platforms, and IT infrastructure. These providers receive only the information necessary to perform services on our behalf and are contractually required to safeguard your information. Mobile opt-in data and SMS consent are never shared with third parties or affiliates for their own marketing or promotional purposes.</li>
              <li><strong>Technology Partners:</strong> Software and hardware vendors necessary for service provision</li>
              <li><strong>Legal Requirements:</strong> Law enforcement or government agencies when required by law</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
              <li><strong>Your Consent:</strong> With your explicit permission for specific purposes</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">SMS Privacy</h2>
            <p className="text-slate-600 mb-4">
              We respect your privacy. We only use information you provide through our SMS service to transmit your mobile messages and respond to you. This includes, but is not limited to, sharing information with communications platform providers, phone companies, and vendors who assist us in the delivery of text messages.
            </p>
            <p className="text-slate-600 mb-4">
              Mobile information will not be shared with third parties or affiliates for marketing or promotional purposes.
            </p>
            <p className="text-slate-600 mb-4">
              All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
            </p>
            <p className="text-slate-600 mb-4">
              We may disclose information as required by law or to protect our legal rights.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Multi-factor authentication and access controls</li>
              <li>Regular security assessments and audits</li>
              <li>Employee training on data protection</li>
              <li>Network monitoring and intrusion detection</li>
              <li>Secure backup and disaster recovery procedures</li>
            </ul>
            <p className="text-slate-600 mb-4">
              While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Data Retention</h2>
            <p className="text-slate-600 mb-4">
              We retain your personal information for as long as necessary to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide our services and fulfill contractual obligations</li>
              <li>Comply with legal, tax, and accounting requirements</li>
              <li>Resolve disputes and enforce our agreements</li>
              <li>Maintain business records for operational purposes</li>
            </ul>
            <p className="text-slate-600 mb-4">
              When information is no longer needed, we securely delete or anonymize it in accordance with our data retention policies.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Your Privacy Rights</h2>
            <p className="text-slate-600 mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Request corrections to inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-Out:</strong> Decline marketing communications and SMS messages</li>
              <li><strong>Data Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Object:</strong> Object to certain processing of your information</li>
            </ul>
            <p className="text-slate-600 mb-4">
              To exercise these rights, please contact us at <a href="mailto:info@mimoit.com" className="text-mimo-primary hover:text-mimo-accent">info@mimoit.com</a>.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-slate-600 mb-4">
              We use cookies and similar technologies to enhance your website experience:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for website functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand website usage</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
            <p className="text-slate-600 mb-4">
              You can control cookie settings through your browser preferences. Disabling cookies may affect website functionality.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Third-Party Links</h2>
            <p className="text-slate-600 mb-4">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing personal information.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Children's Privacy</h2>
            <p className="text-slate-600 mb-4">
              Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children. If we discover we have collected information from a child, we will delete it promptly.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">California Privacy Rights</h2>
            <p className="text-slate-600 mb-4">
              California residents have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or shared</li>
              <li>Right to opt out of the sale of personal information</li>
              <li>Right to deletion of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-slate-600 mb-4">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date. Significant changes will be communicated via email or website notification. Your continued use of our services after changes indicates acceptance of the updated policy.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <p className="text-slate-900 font-semibold mb-2">mimoIT Technology Solutions</p>
              <p className="text-slate-600">Email: <a href="mailto:info@mimoit.com" className="text-mimo-primary hover:text-mimo-accent">info@mimoit.com</a></p>
              <p className="text-slate-600">Phone: <a href="tel:201-473-4443" className="text-mimo-primary hover:text-mimo-accent">201-473-4443</a></p>
              <p className="text-slate-600">Web: <Link to="/contact" className="text-mimo-primary hover:text-mimo-accent">www.mimoit.com/contact</Link></p>
            </div>

          </div>

          {/* Related Pages */}
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to="/terms" className="btn-outline inline-flex items-center gap-2">
              Terms of Service
            </Link>
            <Link to="/sms-terms" className="btn-outline inline-flex items-center gap-2">
              SMS Terms & Conditions
            </Link>
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              Return to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
